import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { COLORS, ICONS, unit } from '@hcr/ui'
import { filter, flow, isEmpty, isNotEmpty, isNotNull, isNotUndefined } from '@hcr/utils'

import { useCouponsContentQuery, useCouponsQuery, useIdToken } from '../../../hooks'

import { createCouponPreview } from './common'
import { CouponsLoading } from './CouponsLoading'
import { prepareCouponPreviewsData } from './utils'

export const Coupons = () => {
  const idToken = useIdToken()
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)

  const coupons = useCouponsQuery(
    { idToken: String(idToken) },
    {
      enabled: isNotNull(idToken),
    }
  )

  const hasCoupons = coupons.isSuccess && isNotUndefined(coupons.data) && isNotEmpty(coupons.data)

  const couponsContent = useCouponsContentQuery({
    select: flow(filter(({ couponType }) => (coupons.data || []).some(coupon => coupon.coupon_type === couponType))),
    enabled: isNotNull(idToken) && hasCoupons,
  })

  const couponsPreview = useMemo(() => createCouponPreview(), [])

  const couponsPreviewData = useMemo(
    () => prepareCouponPreviewsData([coupons.data].filter(isNotUndefined)),
    [coupons.data]
  )

  if (!hasCoupons) {
    return null
  }

  if (coupons.isPending || couponsContent.isPending) {
    return <CouponsLoading />
  }

  if (couponsContent.isError || (couponsContent.isSuccess && isEmpty(couponsContent.data))) {
    return null
  }

  const couponsWithContent = couponsPreviewData.filter(coupon =>
    couponsContent.data.some(content => content.couponType === coupon.coupon_type)
  )

  const handleAccordionChange = () => {
    setExpanded(!expanded)
  }

  const renderAccordionSummary = (isExpanded: boolean) => (
    <AccordionSummary
      expandIcon={<FontAwesomeIcon color={COLORS.burgundi[500]} icon={ICONS.farArrowDown} />}
      aria-controls='additional-coupons-content'
      id='additional-coupons-header'
      aria-expanded={isExpanded}
      sx={{
        width: 'auto',
        justifySelf: 'center',
        textDecoration: 'underline',
        color: COLORS.burgundi[500],
        '&:focus-visible': {
          outline: `2px solid ${COLORS.burgundi[500]}`,
          outlineOffset: '2px',
        },
      }}
    >
      <Typography variant='labelM' textAlign='center' marginRight={unit(2)}>
        {isExpanded ? t('coupons.show-less') : t('coupons.show-more')}
      </Typography>
    </AccordionSummary>
  )

  return (
    <>
      <Typography variant='headlineM' paddingLeft={unit(5)}>
        {t('coupons.coupons')}
      </Typography>

      {couponsWithContent.slice(0, 2).map(couponsPreview)}

      {couponsWithContent.length > 2 && (
        <Accordion expanded={expanded} onChange={handleAccordionChange} disableGutters elevation={0}>
          {!expanded && renderAccordionSummary(false)}

          <AccordionDetails sx={{ padding: 0 }}>
            {couponsWithContent.slice(2).map((coupon, index) => (
              <Box key={index} style={{ marginBottom: unit(3) }}>
                {couponsPreview(coupon)}
              </Box>
            ))}

            {expanded && renderAccordionSummary(true)}
          </AccordionDetails>
        </Accordion>
      )}
    </>
  )
}
